import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { MessageSquare } from 'lucide-react';
import { ChatSidebar, Chat } from './ChatSidebar';
import { TopBar, Scope } from './TopBar';
import { ChatMessages } from './ChatMessages';
import { MessageInput, MessageContext } from './MessageInput';
import { ReviewActions } from './ReviewActions';
import { Profile } from './Profile';
import { Sheet, SheetContent } from './ui/sheet';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { Message, ChatMessages as ChatMessagesType } from '../types/chat';
import { Person } from '../types/profile';
import { getBotReply, generateTitleFromQuestion } from '../lib/assistant';
import { useReviews } from '../context/ReviewsContext';
import { useConversations } from '../context/ConversationsContext';
import { useSystemNotes } from '../context/SystemNotesContext';
import { AlertCircle } from 'lucide-react';

interface AppShellProps {
  children?: React.ReactNode;
}

export const AppShell: React.FC<AppShellProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeChatId, setActiveChatId] = useState<string | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [scope, setScope] = useState<Scope>('default');
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [activePersonName, setActivePersonName] = useState<string | null>(null);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [chatTitle, setChatTitle] = useState<string | null>(null);
  const { addReview, approveReview, rejectReview, requestClarification } = useReviews();
  const { conversations, updateConversationReview, updateConversationAnswer } = useConversations();
  const { getRelevantNotes, markNoteUsed } = useSystemNotes();

  // Helper to navigate away from analytics if needed
  const ensureOnMainPage = useCallback(() => {
    if (location.pathname === '/analytics') {
      navigate('/', { replace: true });
    }
  }, [location.pathname, navigate]);

  // Mock chat data
  const [chats, setChats] = useState<Chat[]>([
    {
      id: '1',
      title: 'Getting started with the assistant',
      updatedAt: new Date(Date.now() - 1000 * 60 * 30), // 30 minutes ago
    },
    {
      id: '2',
      title: 'Project requirements discussion',
      updatedAt: new Date(Date.now() - 1000 * 60 * 60 * 2), // 2 hours ago
    },
    {
      id: '3',
      title: 'Technical architecture review',
      updatedAt: new Date(Date.now() - 1000 * 60 * 60 * 24), // 1 day ago
    },
    {
      id: '4',
      title: 'API integration questions',
      updatedAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 2), // 2 days ago
    },
    {
      id: '5',
      title: 'Deployment strategy',
      updatedAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 5), // 5 days ago
    },
  ]);

  // Messages state - keyed by chat ID
  const [messages, setMessages] = useState<ChatMessagesType>({});

  const handleNewChat = useCallback(() => {
    ensureOnMainPage();
    const newChat: Chat = {
      id: `chat-${Date.now()}`,
      title: 'New chat',
      updatedAt: new Date(),
    };
    setChats(prev => [newChat, ...prev]);
    setMessages(prev => ({ ...prev, [newChat.id]: [] }));
    setActiveChatId(newChat.id);
    setActiveConversationId(null);
    setActivePersonName(null);
    setChatTitle(null);
    setIsMobileMenuOpen(false);
  }, [ensureOnMainPage]);

  const handleChatSelect = useCallback((chatId: string) => {
    ensureOnMainPage();
    setActiveChatId(chatId);
    setActiveConversationId(null);
    setActivePersonName(null);
    setIsMobileMenuOpen(false);
    // Reset chat title when selecting a chat - will be updated by useEffect
    setChatTitle(null);
  }, [ensureOnMainPage]);

  const handlePersonSelect = useCallback((personName: string) => {
    // Get all conversations for this person, sorted by time (oldest first for chronological chat)
    const personConversations = conversations
      .filter(c => c.askedBy === personName)
      .sort((a, b) => a.answeredAt.getTime() - b.answeredAt.getTime());

    if (personConversations.length === 0) return;

    ensureOnMainPage();
    setActivePersonName(personName);
    setActiveConversationId(null);
    setActiveChatId(null);
    setIsMobileMenuOpen(false);

    // Convert all conversations to messages format - showing as one continuous chat
    const conversationMessages: Message[] = [];
    personConversations.forEach((conversation) => {
      // Add user question
      conversationMessages.push({
        id: `msg-${conversation.id}-user`,
        content: conversation.question,
        role: 'user',
        timestamp: conversation.answeredAt,
      });
      // Add assistant answer
      conversationMessages.push({
        id: `msg-${conversation.id}-assistant`,
        content: conversation.answer,
        role: 'assistant',
        timestamp: new Date(conversation.answeredAt.getTime() + 1000), // Slightly after question
        sources: conversation.sources?.map(s => ({
          id: `src-${s.type}-${s.title}`,
          type: s.type as any,
          title: s.title,
          url: s.url,
        })),
        confidenceLevel: conversation.confidenceLevel,
        humanNotified: conversation.reviewRequested,
        notifiedPerson: conversation.mentionedPerson,
        mentionedPerson: conversation.mentionedPerson,
      });
    });

    // Use a virtual chat ID for the person
    const virtualChatId = `person-${personName.toLowerCase()}`;
    setMessages(prev => ({
      ...prev,
      [virtualChatId]: conversationMessages,
    }));
    setActiveChatId(virtualChatId);
    // Use the first conversation's title or a generic title
    const chatTitle = personConversations[0]?.title || `Conversation with ${personName}`;
    setChatTitle(chatTitle);
  }, [conversations, ensureOnMainPage]);

  const handleConversationSelect = useCallback((conversationId: string) => {
    const conversation = conversations.find(c => c.id === conversationId);
    if (!conversation) return;

    ensureOnMainPage();
    setActiveConversationId(conversationId);
    setActivePersonName(null);
    setActiveChatId(null);
    setIsMobileMenuOpen(false);

    // Convert conversation to messages format
    const conversationMessages: Message[] = [
      {
        id: `msg-${conversation.id}-user`,
        content: conversation.question,
        role: 'user',
        timestamp: conversation.answeredAt,
      },
      {
        id: `msg-${conversation.id}-assistant`,
        content: conversation.answer,
        role: 'assistant',
        timestamp: conversation.answeredAt,
        sources: conversation.sources?.map(s => ({
          id: `src-${s.type}-${s.title}`,
          type: s.type as any,
          title: s.title,
          url: s.url,
        })),
        confidenceLevel: conversation.confidenceLevel,
        humanNotified: conversation.reviewRequested,
        notifiedPerson: conversation.mentionedPerson,
      },
    ];

    // Use conversation's chatId or create a virtual one
    const virtualChatId = `conv-${conversation.id}`;
    setMessages(prev => ({
      ...prev,
      [virtualChatId]: conversationMessages,
    }));
    setActiveChatId(virtualChatId);
  }, [conversations, ensureOnMainPage]);

  const handleApproveReview = useCallback((conversationId: string, response: string) => {
    const conversation = conversations.find(c => c.id === conversationId);
    if (!conversation || !conversation.reviewId) return;

    approveReview(conversation.reviewId, response);
    updateConversationReview(conversation.chatId, conversation.reviewId, 'approved');
    // Update the conversation's answer so it persists
    updateConversationAnswer(conversationId, response);

    // Replace the last assistant message (the one that triggered the review) with the approved response
    const assistantMessageId = `msg-${conversationId}-assistant`;

    // Check if we're viewing a person's conversation or a single conversation
    const virtualChatId = activePersonName
      ? `person-${activePersonName.toLowerCase()}`
      : `conv-${conversationId}`;

    setMessages(prev => {
      const currentMessages = prev[virtualChatId] || [];
      const messageIndex = currentMessages.findIndex(msg => msg.id === assistantMessageId);

      if (messageIndex !== -1) {
        // Replace the existing assistant message with the approved response
        const updatedMessages = [...currentMessages];
        updatedMessages[messageIndex] = {
          ...updatedMessages[messageIndex],
          content: response,
          humanNotified: false, // No longer needs human review
          notifiedPerson: undefined, // Clear notification status
        };
        return {
          ...prev,
          [virtualChatId]: updatedMessages,
        };
      }

      // If message not found, add it as a new message (fallback)
      return {
        ...prev,
        [virtualChatId]: [...currentMessages, {
          id: assistantMessageId,
          content: response,
          role: 'assistant',
          timestamp: new Date(),
        }],
      };
    });
  }, [conversations, approveReview, updateConversationReview, updateConversationAnswer, activePersonName]);

  const handleRejectReview = useCallback((conversationId: string) => {
    const conversation = conversations.find(c => c.id === conversationId);
    if (!conversation || !conversation.reviewId) return;

    rejectReview(conversation.reviewId);
    updateConversationReview(conversation.chatId, conversation.reviewId, 'rejected');
  }, [conversations, rejectReview, updateConversationReview]);

  const handleRequestClarificationReview = useCallback((conversationId: string, clarification: string) => {
    const conversation = conversations.find(c => c.id === conversationId);
    if (!conversation || !conversation.reviewId) return;

    requestClarification(conversation.reviewId, clarification);
    updateConversationReview(conversation.chatId, conversation.reviewId, 'clarification-requested');
  }, [conversations, requestClarification, updateConversationReview]);

  const handleTalkToAssistant = useCallback((person: Person) => {
    // Create a new chat for talking to the person's assistant
    const newChat: Chat = {
      id: `chat-${Date.now()}`,
      title: `Chat with ${person.name}'s Assistant`,
      updatedAt: new Date(),
    };
    setChats(prev => [newChat, ...prev]);
    setMessages(prev => ({ ...prev, [newChat.id]: [] }));
    setActiveChatId(newChat.id);
    setChatTitle(newChat.title);
    setActiveConversationId(null);
  }, []);

  const handleSendMessage = useCallback(async (content: string, context?: MessageContext) => {
    // If no active chat, create a new one first
    // Context can be used to filter sources or mention specific people/bots
    let chatIdToUse = activeChatId;
    if (!chatIdToUse) {
      const newChat: Chat = {
        id: `chat-${Date.now()}`,
        title: 'New chat',
        updatedAt: new Date(),
      };
      setChats(prev => [newChat, ...prev]);
      setMessages(prev => ({ ...prev, [newChat.id]: [] }));
      setActiveChatId(newChat.id);
      setChatTitle(null);
      chatIdToUse = newChat.id;
    }

    const userMessage: Message = {
      id: `msg-${Date.now()}-user`,
      content,
      role: 'user',
      timestamp: new Date(),
    };

    // Add user message
    setMessages(prev => ({
      ...prev,
      [chatIdToUse]: [...(prev[chatIdToUse] || []), userMessage],
    }));

    // Update chat title if it's the first message
    setChats(prev => prev.map(chat => {
      if (chat.id === chatIdToUse && chat.title === 'New chat') {
        // Generate a better title from the question
        const newTitle = generateTitleFromQuestion(content, 60);
        setChatTitle(newTitle);
        return { ...chat, title: newTitle, updatedAt: new Date() };
      }
      return chat;
    }));

    // Check if this is an AI API Usage Policy question
    const lowerContent = content.toLowerCase().trim();
    const isAIAPIPolicyQuestion =
      lowerContent.includes('ai api usage') ||
      lowerContent.includes('ai api policy') ||
      lowerContent.includes('approving ai api') ||
      (lowerContent.includes('ai api') && (lowerContent.includes('production') || lowerContent.includes('policy') || lowerContent.includes('approval'))) ||
      (lowerContent.includes('policy') && lowerContent.includes('ai api') && lowerContent.includes('production'));

    if (isAIAPIPolicyQuestion) {
      setIsLoading(true);

      const placeholderIds: string[] = [];

      // Step 1: Checking OneDrive
      const step1Message: Message = {
        id: `msg-${Date.now()}-step1`,
        content: 'Checking available OneDrive files...',
        role: 'assistant',
        timestamp: new Date(),
        isContacting: true,
      };
      placeholderIds.push(step1Message.id);
      setMessages(prev => ({
        ...prev,
        [chatIdToUse]: [...(prev[chatIdToUse] || []), step1Message],
      }));
      await new Promise(resolve => setTimeout(resolve, 800));

      // Step 2: Checking SharePoint
      const step2Message: Message = {
        id: `msg-${Date.now()}-step2`,
        content: 'Checking published SharePoint...',
        role: 'assistant',
        timestamp: new Date(),
        isContacting: true,
      };
      placeholderIds.push(step2Message.id);
      setMessages(prev => ({
        ...prev,
        [chatIdToUse]: [...(prev[chatIdToUse] || []), step2Message],
      }));
      await new Promise(resolve => setTimeout(resolve, 800));

      // Step 3: Checking organization policies
      const step3Message: Message = {
        id: `msg-${Date.now()}-step3`,
        content: "Checking organization-published policies...",
        role: 'assistant',
        timestamp: new Date(),
        isContacting: true,
      };
      placeholderIds.push(step3Message.id);
      setMessages(prev => ({
        ...prev,
        [chatIdToUse]: [...(prev[chatIdToUse] || []), step3Message],
      }));
      await new Promise(resolve => setTimeout(resolve, 1000));

      try {
        // Get relevant system notes for this question
        const relevantNotes = getRelevantNotes(content);

        // Get assistant reply with system notes
        const botReply = await getBotReply(content, undefined, relevantNotes);

        // Mark system notes as used
        if (botReply.systemNotesUsed) {
          botReply.systemNotesUsed.forEach(note => markNoteUsed(note.id));
        }

        // Remove all placeholder messages and add the actual response
        setMessages(prev => ({
          ...prev,
          [chatIdToUse]: (prev[chatIdToUse] || [])
            .filter(msg => !placeholderIds.includes(msg.id))
            .concat({
              id: `msg-${Date.now()}-assistant`,
              content: botReply.content,
              role: 'assistant',
              timestamp: new Date(),
              sources: botReply.sources,
              confidenceLevel: botReply.confidenceLevel,
              followUpActions: botReply.followUpActions,
              humanNotified: botReply.humanNotified,
              notifiedPerson: botReply.notifiedPerson,
            }),
        }));

        // Update chat updatedAt
        setChats(prev => {
          return prev.map(chat =>
            chat.id === chatIdToUse
              ? { ...chat, updatedAt: new Date() }
              : chat
          );
        });
      } catch (error) {
        console.error('Error getting assistant reply:', error);
        // Remove placeholder messages and show error
        setMessages(prev => ({
          ...prev,
          [chatIdToUse]: (prev[chatIdToUse] || [])
            .filter(msg => !placeholderIds.includes(msg.id))
            .concat({
              id: `msg-${Date.now()}-error`,
              content: 'Sorry, I encountered an error. Please try again.',
              role: 'assistant',
              timestamp: new Date(),
            }),
        }));
      } finally {
        setIsLoading(false);
      }
      return;
    }

    // Check for @mentions
    const mentionMatch = content.match(/@(\w+)/i);
    if (mentionMatch) {
      const mentionedPerson = mentionMatch[1];
      const capitalizedName = mentionedPerson.charAt(0).toUpperCase() + mentionedPerson.slice(1).toLowerCase();

      // Show "Contacting..." placeholder
      const contactingMessage: Message = {
        id: `msg-${Date.now()}-contacting`,
        content: `Contacting ${capitalizedName}'s assistant...`,
        role: 'assistant',
        timestamp: new Date(),
        isContacting: true,
        mentionedPerson: capitalizedName,
      };

      setMessages(prev => ({
        ...prev,
        [chatIdToUse]: [...(prev[chatIdToUse] || []), contactingMessage],
      }));

      // Show loading state
      setIsLoading(true);

      // Wait for "contacting" delay (2-3 seconds)
      await new Promise(resolve => setTimeout(resolve, 2000 + Math.random() * 1000));

      try {
        // Get assistant reply with mention context
        // Get relevant system notes for this question
        const relevantNotes = getRelevantNotes(content);

        const botReply = await getBotReply(content, mentionedPerson, relevantNotes);

        // Mark system notes as used
        if (botReply.systemNotesUsed) {
          botReply.systemNotesUsed.forEach(note => markNoteUsed(note.id));
        }

        // Create pending review if human was notified (before setMessages)
        let reviewId: string | undefined;
        if (botReply.humanNotified && botReply.notifiedPerson) {
          const chatMessages = messages[chatIdToUse] || [];
          const previousMessages = chatMessages
            .slice(-3)
            .map(m => `${m.role === 'user' ? 'User' : 'Assistant'}: ${m.content}`)
            .join('\n');

          reviewId = addReview({
            question: content,
            askedBy: 'Current User', // In real app, get from auth context
            askedAt: new Date(),
            mentionedPerson: capitalizedName,
            context: previousMessages || undefined,
            chatId: chatIdToUse,
          });
        }

        // Remove the contacting message and add the actual response
        setMessages(prev => ({
          ...prev,
          [chatIdToUse]: (prev[chatIdToUse] || []).filter(msg => msg.id !== contactingMessage.id).concat({
            id: `msg-${Date.now()}-assistant`,
            content: botReply.content,
            role: 'assistant',
            timestamp: new Date(),
            sources: botReply.sources,
            confidenceLevel: botReply.confidenceLevel,
            followUpActions: botReply.followUpActions,
            humanNotified: botReply.humanNotified,
            notifiedPerson: botReply.notifiedPerson,
            mentionedPerson: capitalizedName, // Indicate this response came from the mentioned person's assistant
          }),
        }));

        // Update chat updatedAt
        setChats(prev => prev.map(chat =>
          chat.id === chatIdToUse
            ? { ...chat, updatedAt: new Date() }
            : chat
        ));
      } catch (error) {
        console.error('Error getting assistant reply:', error);
        // Remove contacting message and show error
        setMessages(prev => ({
          ...prev,
          [chatIdToUse]: (prev[chatIdToUse] || []).filter(msg => msg.id !== contactingMessage.id).concat({
            id: `msg-${Date.now()}-error`,
            content: 'Sorry, I encountered an error contacting the assistant. Please try again.',
            role: 'assistant',
            timestamp: new Date(),
          }),
        }));
      } finally {
        setIsLoading(false);
      }
      return;
    }

    // Regular assistant reply flow (no mentions)
    setIsLoading(true);

    try {
      // Get assistant reply
      // Get relevant system notes for this question
      const relevantNotes = getRelevantNotes(content);

      const botReply = await getBotReply(content, undefined, relevantNotes);

      // Mark system notes as used
      if (botReply.systemNotesUsed) {
        botReply.systemNotesUsed.forEach(note => markNoteUsed(note.id));
      }

      const botMessage: Message = {
        id: `msg-${Date.now()}-assistant`,
        content: botReply.content,
        role: 'assistant',
        timestamp: new Date(),
        sources: botReply.sources,
        confidenceLevel: botReply.confidenceLevel,
        followUpActions: botReply.followUpActions,
        humanNotified: botReply.humanNotified,
        notifiedPerson: botReply.notifiedPerson,
      };

      // Add assistant message and create review if needed
      let reviewId: string | undefined;
      if (botReply.humanNotified && botReply.notifiedPerson) {
        const chatMessages = messages[chatIdToUse] || [];
        const previousMessages = chatMessages
          .slice(-3)
          .map(m => `${m.role === 'user' ? 'User' : 'Assistant'}: ${m.content}`)
          .join('\n');

        reviewId = addReview({
          question: content,
          askedBy: 'Current User',
          askedAt: new Date(),
          mentionedPerson: botReply.notifiedPerson,
          context: previousMessages || undefined,
          chatId: chatIdToUse,
        });
      }

      setMessages(prev => ({
        ...prev,
        [chatIdToUse]: [...(prev[chatIdToUse] || []), botMessage],
      }));

      // Update chat updatedAt
      setChats(prev => prev.map(chat =>
        chat.id === chatIdToUse
          ? { ...chat, updatedAt: new Date() }
          : chat
      ));
    } catch (error) {
      console.error('Error getting assistant reply:', error);
      const errorMessage: Message = {
        id: `msg-${Date.now()}-error`,
        content: 'Sorry, I encountered an error. Please try again.',
        role: 'assistant',
        timestamp: new Date(),
      };
      setMessages(prev => ({
        ...prev,
        [chatIdToUse]: [...(prev[chatIdToUse] || []), errorMessage],
      }));
    } finally {
      setIsLoading(false);
    }
  }, [activeChatId, addReview, messages, conversations, approveReview, rejectReview, requestClarification, updateConversationReview, setActiveChatId, setChats, setMessages, setChatTitle, getRelevantNotes, markNoteUsed]);

  const activeChat = chats.find(chat => chat.id === activeChatId);
  const activeConversation = activeConversationId
    ? conversations.find(c => c.id === activeConversationId)
    : null;

  // Update chat title when activeChatId changes
  useEffect(() => {
    if (activeChatId && !chatTitle) {
      const chat = chats.find(c => c.id === activeChatId);
      if (chat && chat.title !== 'New chat') {
        setChatTitle(chat.title);
      }
    }
  }, [activeChatId, chats, chatTitle]);

  const sidebarContent = (
    <ChatSidebar
      chats={chats}
      activeChatId={activeChatId}
      onChatSelect={handleChatSelect}
      onNewChat={handleNewChat}
      conversations={conversations}
      activeConversationId={activeConversationId}
      onConversationSelect={handleConversationSelect}
      onPersonSelect={handlePersonSelect}
      activePersonName={activePersonName}
      onProfileClick={() => setIsProfileOpen(true)}
    />
  );

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex md:w-[280px] lg:w-[320px] flex-shrink-0">
        {sidebarContent}
      </aside>

      {/* Mobile Sidebar (Sheet) */}
      <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
        <SheetContent side="left" className="w-[280px] p-0">
          {sidebarContent}
        </SheetContent>
      </Sheet>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopBar
          chatTitle={chatTitle || (activePersonName ? `Conversation with ${activePersonName}` : null) || activeConversation?.title || activeChat?.title || null}
          scope={scope}
          onScopeChange={setScope}
          onMenuClick={() => setIsMobileMenuOpen(true)}
          onProfileClick={() => setIsProfileOpen(true)}
        />

        <main className="flex-1 flex flex-col overflow-hidden">
          {children || (
            <>
              {activeChatId && (messages[activeChatId]?.length > 0 || activeConversationId || activePersonName) ? (
                <>
                  <div className="flex-1 overflow-hidden">
                    <ChatMessages
                      messages={messages[activeChatId] || []}
                    />
                  </div>

                  {/* Review Actions for conversations needing review - Collapsed by default */}
                  {(() => {
                    // Check if we're viewing a person's conversation and find the last pending review
                    if (activePersonName) {
                      const personConversations = conversations
                        .filter(c => c.askedBy === activePersonName)
                        .sort((a, b) => b.answeredAt.getTime() - a.answeredAt.getTime());
                      const lastPendingReview = personConversations.find(
                        c => c.reviewRequested && c.reviewStatus === 'pending'
                      );
                      if (lastPendingReview) {
                        return (
                          <div className="border-t bg-muted/30">
                            <Accordion type="single" collapsible className="w-full" defaultValue="">
                              <AccordionItem value="review-actions" className="border-none">
                                <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-muted/50">
                                  <div className="flex items-center gap-2 flex-1">
                                    <AlertCircle className="h-4 w-4 text-yellow-600 dark:text-yellow-400 flex-shrink-0" />
                                    <span className="text-sm font-medium text-foreground">Draft for Review</span>
                                  </div>
                                </AccordionTrigger>
                                <AccordionContent className="px-0 pb-0">
                                  <ReviewActions
                                    conversationId={lastPendingReview.id}
                                    question={lastPendingReview.question}
                                    mentionedPerson={lastPendingReview.mentionedPerson}
                                    onApprove={(response) => handleApproveReview(lastPendingReview.id, response)}
                                    onReject={() => handleRejectReview(lastPendingReview.id)}
                                    onRequestClarification={(clarification) => handleRequestClarificationReview(lastPendingReview.id, clarification)}
                                    className="border-t-0"
                                  />
                                </AccordionContent>
                              </AccordionItem>
                            </Accordion>
                          </div>
                        );
                      }
                    }
                    // Check if we're viewing a single conversation
                    if (activeConversationId) {
                      const conversation = conversations.find(c => c.id === activeConversationId);
                      if (conversation?.reviewRequested && conversation.reviewStatus === 'pending') {
                        return (
                          <div className="border-t bg-muted/30">
                            <Accordion type="single" collapsible className="w-full" defaultValue="">
                              <AccordionItem value="review-actions" className="border-none">
                                <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-muted/50">
                                  <div className="flex items-center gap-2 flex-1">
                                    <AlertCircle className="h-4 w-4 text-yellow-600 dark:text-yellow-400 flex-shrink-0" />
                                    <span className="text-sm font-medium text-foreground">Draft for Review</span>
                                  </div>
                                </AccordionTrigger>
                                <AccordionContent className="px-0 pb-0">
                                  <ReviewActions
                                    conversationId={activeConversationId}
                                    question={conversation.question}
                                    mentionedPerson={conversation.mentionedPerson}
                                    onApprove={(response) => handleApproveReview(activeConversationId, response)}
                                    onReject={() => handleRejectReview(activeConversationId)}
                                    onRequestClarification={(clarification) => handleRequestClarificationReview(activeConversationId, clarification)}
                                    className="border-t-0"
                                  />
                                </AccordionContent>
                              </AccordionItem>
                            </Accordion>
                          </div>
                        );
                      }
                    }
                    return null;
                  })()}

                  {/* Message Input - bottom aligned when chat is active */}
                  <MessageInput
                    onSendMessage={handleSendMessage}
                    disabled={isLoading}
                  />
                </>
              ) : (
                /* Empty state with centered input */
                <div className="flex-1 flex flex-col items-center justify-center p-6">
                  <div className="w-full max-w-2xl flex flex-col items-center">
                    <div className="mb-12 text-center space-y-3">
                      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
                        <MessageSquare className="h-8 w-8 text-primary" />
                      </div>
                      <h2 className="text-2xl font-bold text-foreground leading-tight">
                        Your assistant can reason, delegate, and synthesize across your team.
                      </h2>
                      <p className="text-base text-muted-foreground max-w-md mt-2">
                        Start a conversation by typing a message below
                      </p>
                    </div>
                    <div className="w-full max-w-xl">
                      <MessageInput
                        onSendMessage={handleSendMessage}
                        disabled={isLoading}
                        className="border-0 shadow-md rounded-xl bg-background"
                        placeholder="Ask me anything..."
                      />
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </main>
      </div>

      {/* Profile Modal */}
      <Profile
        isOpen={isProfileOpen}
        onClose={() => setIsProfileOpen(false)}
        onTalkToAssistant={handleTalkToAssistant}
        currentUserName="Muhammad"
      />
    </div>
  );
};

